import type { MouseEventHandler, ReactNode } from "react";

import { cn } from "@/utils/cn";

// Figma "text link" (3506:6060): an underlined uppercase action, with an optional trailing icon.
// The electric tone is the footer's "Give $20, Get $20" (HJIp5P6tyeFrUd7ODqeWdM 4180:18962).

export const TEXT_LINK_TONES = ["black", "electric"] as const;

export type TextLinkTone = (typeof TEXT_LINK_TONES)[number];

type TextLinkProps = {
  children: ReactNode;
  /** Trailing icon, sized size-ds-icon-small by the caller. */
  icon?: ReactNode;
  /** electric is for dark backgrounds. */
  tone?: TextLinkTone;
  /** Renders an `<a>` instead of a `<button>`. */
  href?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Layout only. A repeated look is a variant. */
  className?: string;
};

// `unset` opts out of app.css `button { font-size: 12px }`, which otherwise beats these sizes.
const BASE = cn(
  "unset inline-flex w-fit items-center gap-ds-space-02 border-b-2 pb-ds-space-02",
  "text-ds-header-subheader uppercase md:text-ds-header-subheader-desktop"
);

const TONES: Record<TextLinkTone, string> = {
  black: "border-ds-ui-elements-black text-ds-ui-elements-black",
  electric: "border-ds-all-colors-electric text-ds-all-colors-electric",
};

// Button or anchor, so there is no single ref to forward. See ui/README.md.
export const TextLink = ({
  children,
  icon,
  tone = "black",
  href,
  onClick,
  className,
}: TextLinkProps) => {
  const classNames = cn(BASE, TONES[tone], className);
  if (href) {
    return (
      <a href={href} className={classNames}>
        {children}
        {icon}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classNames}>
      {children}
      {icon}
    </button>
  );
};
